import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import { adminService } from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import PricingTable from './components/PricingTable';
import PlanBuilderModal from './components/PlanBuilderModal';
import FeatureMatrixCard from './components/FeatureMatrixCard';
import PromotionalTools from './components/PromotionalTools';
import RevenueCalculator from './components/RevenueCalculator';
import { PLAN_ORDER } from '../../config/planFeatures';
import { getCanonicalPricingTiers } from '../../config/pricingTiers';

const PricingPlansManagement = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [catalogError, setCatalogError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [showPlanBuilder, setShowPlanBuilder] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [activeTab, setActiveTab] = useState('plans');
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/authentication');
      return;
    }
    checkAdminAndLoadData();
  }, [user, authLoading, navigate]);

  const checkAdminAndLoadData = async () => {
    try {
      setLoading(true);
      const adminStatus = await adminService?.isAdmin();
      if (!adminStatus) {
        toast?.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
        return;
      }
      setIsAdmin(true);
      await loadPricingTiers();
    } catch (error) {
      console.error('Error checking admin status:', error);
      toast?.error('Failed to verify admin access');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadPricingTiers = async () => {
    try {
      setCatalogError('');
      // Use admin version to load ALL plans (including inactive)
      const data = await adminService?.getAllPricingTiersAdmin();
      const canonicalTiers = getCanonicalPricingTiers(data || [], { includeMissing: false });
      if (canonicalTiers.length !== PLAN_ORDER.length) {
        throw new Error('The canonical pricing catalog is incomplete.');
      }
      setPricingTiers(canonicalTiers);
    } catch (error) {
      console.error('Error loading pricing tiers:', error);
      setPricingTiers([]);
      setCatalogError('The four canonical pricing plans are unavailable. Refresh to retry; if this persists, restore the pricing catalog migration.');
      toast?.error('The canonical pricing catalog could not be loaded');
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setShowPlanBuilder(true);
  };

  const handleDeletePlan = async (planId) => {
    const plan = pricingTiers.find((tier) => tier.id === planId);
    if (PLAN_ORDER.includes(plan?.planKey)) {
      toast?.error('Canonical pricing plans cannot be deleted. Deactivate a plan instead.');
      return;
    }
    if (!confirm('Are you sure you want to delete this pricing plan? This cannot be undone.')) return;
    try {
      await adminService?.deletePricingTier(planId);
      toast?.success('Pricing plan deleted successfully');
      await loadPricingTiers();
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast?.error('Failed to delete pricing plan');
    }
  };

  const handleToggleActive = async (tier) => {
    const newStatus = !tier?.isActive;
    // Optimistic update
    setPricingTiers(prev =>
      prev?.map(t => t?.id === tier?.id ? { ...t, isActive: newStatus } : t)
    );
    try {
      await adminService?.updatePricingTier(tier?.id, { isActive: newStatus });
      toast?.success(`Plan ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling plan status:', error);
      toast?.error('Failed to update plan status');
      // Revert on error
      setPricingTiers(prev =>
        prev?.map(t => t?.id === tier?.id ? { ...t, isActive: tier?.isActive } : t)
      );
    }
  };

  const handleSavePlan = async (planData) => {
    try {
      if (editingPlan) {
        await adminService?.updatePricingTier(editingPlan?.id, planData);
        toast?.success('Pricing plan updated successfully');
      } else {
        await adminService?.createPricingTier(planData);
        toast?.success('Pricing plan created successfully');
      }
      await loadPricingTiers();
      setShowPlanBuilder(false);
      setEditingPlan(null);
    } catch (error) {
      console.error('Error saving plan:', error);
      toast?.error(`Failed to save pricing plan: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleReorder = async (reorderedTiers) => {
    setPricingTiers(reorderedTiers);
    setIsSavingOrder(true);
    try {
      await Promise.all(
        reorderedTiers?.map((tier, index) =>
          adminService?.updatePricingTier(tier?.id, { sortOrder: index + 1 })
        )
      );
      toast?.success('Plan order saved');
    } catch (error) {
      console.error('Error saving plan order:', error);
      toast?.error('Failed to save plan order');
      await loadPricingTiers();
    } finally {
      setIsSavingOrder(false);
    }
  };

  const tabs = [
    { id: 'plans', label: 'Pricing Plans', icon: 'DollarSign' },
    { id: 'features', label: 'Feature Matrix', icon: 'Grid3x3' },
    { id: 'promotions', label: 'Promotions', icon: 'Tag' },
    { id: 'calculator', label: 'Revenue Impact', icon: 'Calculator' },
  ];

  if (loading || authLoading) {
    return (
      <NavigationBar stats={{}}>
        <div className="flex items-center justify-center min-h-screen">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </NavigationBar>
    );
  }

  if (!isAdmin) return null;

  return (
    <NavigationBar stats={{}}>
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="mb-6">
              <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                  Pricing & Plans Management
                </h1>
                <p className="text-muted-foreground">Configure the four approved subscription tiers, features, and promotional campaigns</p>
              </div>
              <div className="flex items-center gap-3">
                {isSavingOrder && (
                  <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Icon name="Loader2" size={14} className="animate-spin" />
                    Saving order…
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-border mb-6">
            <div className="flex gap-4">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div>
            {catalogError ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                <p className="text-sm text-destructive">{catalogError}</p>
                <button
                  type="button"
                  onClick={loadPricingTiers}
                  className="mt-3 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
                >
                  Retry loading plans
                </button>
              </div>
            ) : activeTab === 'plans' && (
              <PricingTable
                pricingTiers={pricingTiers}
                onEdit={handleEditPlan}
                onDelete={handleDeletePlan}
                onReorder={handleReorder}
                onToggleActive={handleToggleActive}
              />
            )}
            {activeTab === 'features' && (
              <FeatureMatrixCard pricingTiers={pricingTiers} />
            )}
            {activeTab === 'promotions' && (
              <PromotionalTools />
            )}
            {activeTab === 'calculator' && (
              <RevenueCalculator pricingTiers={pricingTiers} />
            )}
          </div>
        </div>
      </div>

      {showPlanBuilder && (
        <PlanBuilderModal
          plan={editingPlan}
          onClose={() => { setShowPlanBuilder(false); setEditingPlan(null); }}
          onSave={handleSavePlan}
        />
      )}
    </NavigationBar>
  );
};

export default PricingPlansManagement;
